/**
 * AuthorityManagementPage Component
 * 권한 설정 페이지
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useAuthorityManagement } from '../hooks/useAuthorityManagement';
import {
    Authority,
    AuthorityFormData,
    AuthorityFormMode,
    BaseUserType,
} from '../types/authority.types';
import { BASE_USER_TYPE_LABELS } from '../constants/authority.constants';
import { isStandardPermission } from '../utils/authorityUtils';
import { convertPageCodesToLabels } from '../utils/authorityPageUtils';
import AuthorityModal from './AuthorityModal';
import Loading from '@/shared/components/feedback/Loading';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';
import '../styles/AuthorityManagementPage.css';

interface FilterState {
    prmCode: string;
    prmName: string;
}

const INITIAL_FILTERS: FilterState = { prmCode: '', prmName: '' };

const TABLE_HEADERS = [
    { key: 'no', label: 'No', width: '60px' },
    { key: 'prmCode', label: '권한코드', width: '120px' },
    { key: 'prmName', label: '권한명', width: '150px' },
    { key: 'baseUserType', label: '기반 사용자', width: '100px' },
    { key: 'accessPages', label: '접근 페이지', width: 'auto' },
    { key: 'prmDesc', label: '설명', width: '200px' },
    { key: 'actions', label: '관리', width: '120px' },
] as const;

const AuthorityManagementPage: React.FC = () => {
    const {
        loading,
        saving,
        authorities,
        createAuthority,
        updateAuthority,
        deleteAuthority,
    } = useAuthorityManagement();

    const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<AuthorityFormMode>('create');
    const [selectedAuthority, setSelectedAuthority] = useState<Authority | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [authorityToDelete, setAuthorityToDelete] = useState<Authority | null>(null);
    const [infoExpanded, setInfoExpanded] = useState(false);

    const filteredAuthorities = useMemo(() => {
        return authorities.filter(authority => {
            const matchesPrmCode = !filters.prmCode ||
                authority.prmCode.toLowerCase().includes(filters.prmCode.toLowerCase());
            const matchesPrmName = !filters.prmName ||
                authority.prmName.toLowerCase().includes(filters.prmName.toLowerCase());
            return matchesPrmCode && matchesPrmName;
        });
    }, [authorities, filters]);

    const handleFilterChange = useCallback((field: keyof FilterState) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFilters(prev => ({ ...prev, [field]: e.target.value }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
    }, []);

    const handleCreateClick = useCallback(() => {
        setSelectedAuthority(null);
        setModalMode('create');
        setModalOpen(true);
    }, []);

    const handleEditClick = useCallback((authority: Authority) => {
        setSelectedAuthority(authority);
        setModalMode('edit');
        setModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((authority: Authority) => {
        setAuthorityToDelete(authority);
        setDeleteConfirmOpen(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setModalOpen(false);
        setSelectedAuthority(null);
    }, []);

    const handleModalSave = useCallback(async (data: AuthorityFormData) => {
        const success = modalMode === 'create'
            ? await createAuthority(data)
            : await updateAuthority(data);
        if (success) handleModalClose();
    }, [modalMode, createAuthority, updateAuthority, handleModalClose]);

    const handleDeleteConfirm = useCallback(async () => {
        if (authorityToDelete) await deleteAuthority(authorityToDelete.prmCode);
        setDeleteConfirmOpen(false);
        setAuthorityToDelete(null);
    }, [authorityToDelete, deleteAuthority]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmOpen(false);
        setAuthorityToDelete(null);
    }, []);

    const getBaseUserTypeLabel = (type: string): string =>
        BASE_USER_TYPE_LABELS[type as BaseUserType] || type;

    if (loading && authorities.length === 0) {
        return <Loading message="권한 목록을 불러오는 중..." />;
    }

    return (
        <div className="authority-management-page">
            <div className="page-header">
                <h1 className="page-title">권한 설정</h1>
            </div>

            <div className={`info-section ${infoExpanded ? 'expanded' : 'collapsed'}`}>
                <button
                    className="info-toggle-btn"
                    onClick={() => setInfoExpanded(!infoExpanded)}
                    type="button"
                >
                    <i className="fas fa-info-circle" />
                    <span>권한 설정 안내</span>
                    <i className={`fas fa-chevron-${infoExpanded ? 'up' : 'down'} toggle-icon`} />
                </button>
                {infoExpanded && (
                    <div className="info-content">
                        <div className="info-item">
                            <i className="fas fa-info-circle" />
                            <span>
                                <strong className="info-title">표준 권한 안내</strong>
                                표준 권한(A, C, S, R)은 시스템의 기본 권한으로, 수정 및 삭제가 불가능합니다.
                                <ul className="standard-permission-list">
                                    <li><strong>A (관리자)</strong> 시스템 전체 관리 권한</li>
                                    <li><strong>C (본사)</strong> 본사 직원 권한</li>
                                    <li><strong>S (협력사)</strong> 협력사 관리자 권한</li>
                                    <li><strong>R (라이더)</strong> 라이더 기본 권한</li>
                                </ul>
                            </span>
                        </div>
                        <div className="info-item">
                            <i className="fas fa-exclamation-triangle" />
                            <span>
                                <strong className="info-title">주의사항</strong>
                                권한을 삭제하면 해당 권한을 가진 사용자의 접근이 제한될 수 있습니다.
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form authority-search-form">
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-key" />
                                    권한코드
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="권한코드"
                                    value={filters.prmCode}
                                    onChange={handleFilterChange('prmCode')}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-tag" />
                                    권한명
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="권한명"
                                    value={filters.prmName}
                                    onChange={handleFilterChange('prmName')}
                                />
                            </div>
                        </div>
                        <div className="form-group button-group">
                            <button className="btn btn-secondary" onClick={handleResetFilters}>
                                <i className="fas fa-undo" />
                                초기화
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateClick}
                                disabled={saving}
                            >
                                <i className="fas fa-plus" />
                                권한 등록
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="result-count">
                <i className="fas fa-list" />
                총 <strong>{filteredAuthorities.length.toLocaleString()}건</strong>
                {filteredAuthorities.length !== authorities.length && (
                    <span className="filter-info"> (전체 {authorities.length}건 중)</span>
                )}
            </div>

            <div className="table-container authority-table-container">
                <table className="authority-table">
                    <thead>
                    <tr>
                        {TABLE_HEADERS.map(header => (
                            <th
                                key={header.key}
                                style={{ width: header.width, minWidth: header.width !== 'auto' ? header.width : undefined }}
                            >
                                {header.label}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {filteredAuthorities.length === 0 ? (
                        <tr>
                            <td colSpan={TABLE_HEADERS.length} className="text-center text-gray-600">
                                {authorities.length === 0 ? '등록된 권한이 없습니다.' : '검색 결과가 없습니다.'}
                            </td>
                        </tr>
                    ) : (
                        filteredAuthorities.map((authority, index) => {
                            const isStandard = isStandardPermission(authority.prmCode);
                            return (
                                <tr key={authority.prmCode} className={isStandard ? 'row-standard' : ''}>
                                    <td className="text-center">{index + 1}</td>
                                    <td className="text-center">
                                            <span className={`badge ${isStandard ? 'badge-standard' : 'badge-custom'}`}>
                                                {authority.prmCode}
                                            </span>
                                    </td>
                                    <td className="text-left">{authority.prmName}</td>
                                    <td className="text-center">
                                        {authority.baseUserType && (
                                            <span className={`user-type-badge user-type-${authority.baseUserType.toLowerCase()}`}>
                                                    {getBaseUserTypeLabel(authority.baseUserType)}
                                                </span>
                                        )}
                                    </td>
                                    <td className="access-pages-cell">
                                            <span className="access-pages-text" title={authority.accessPagesStr}>
                                                {convertPageCodesToLabels(authority.accessPagesStr || '')}
                                            </span>
                                    </td>
                                    <td className="desc-cell">{authority.prmDesc || '-'}</td>
                                    <td className="text-center">
                                        {!isStandard && (
                                            <div className="action-buttons">
                                                <button
                                                    className="authority-action-btn authority-edit-btn"
                                                    onClick={() => handleEditClick(authority)}
                                                    disabled={saving}
                                                    title="수정"
                                                >
                                                    <i className="fas fa-pen" style={{ color: '#3b82f6' }}></i>
                                                </button>
                                                <button
                                                    className="authority-action-btn authority-delete-btn"
                                                    onClick={() => handleDeleteClick(authority)}
                                                    disabled={saving}
                                                    title="삭제"
                                                >
                                                    <i className="fas fa-trash-alt" style={{ color: '#ef4444' }}></i>
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>

            <AuthorityModal
                isOpen={modalOpen}
                mode={modalMode}
                authority={selectedAuthority}
                onClose={handleModalClose}
                onSave={handleModalSave}
            />

            <ConfirmModal
                isOpen={deleteConfirmOpen}
                title="권한 삭제"
                message={`'${authorityToDelete?.prmName}' 권한을 삭제하시겠습니까?\n이 권한을 가진 사용자의 접근이 제한될 수 있습니다.`}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </div>
    );
};

export default AuthorityManagementPage;
